import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import * as THREE from "three";
import { collection, doc, getDocs, setDoc, query, where } from "firebase/firestore";
import { auth, db } from "../FirebaseConfig";

const platformPricing = {
  instagram: 3000,
  twitter: 2000,
  tiktok: 3000,
};

// Three.js 3D animation setup
// Three.js 3D animation setup
const initThreeJS = (canvas) => {
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(
    75,
    canvas.clientWidth / canvas.clientHeight,
    0.1,
    1000
  );
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
  renderer.setClearColor(0x0f172a); // Dark background so the shape pops
  renderer.setSize(canvas.clientWidth, canvas.clientHeight);

  const geometry = new THREE.OctahedronGeometry(1.5, 0);
  const material = new THREE.MeshStandardMaterial({
    color: 0x10b981,
    metalness: 0.6,
    roughness: 0.3,
  });
  const shape = new THREE.Mesh(geometry, material);
  scene.add(shape);

  // Lights
  scene.add(new THREE.AmbientLight(0xffffff, 0.4));
  const pointLight1 = new THREE.PointLight(0xffffff, 1);
  pointLight1.position.set(5, 5, 5);
  scene.add(pointLight1);

  const pointLight2 = new THREE.PointLight(0x10b981, 1);
  pointLight2.position.set(-5, -5, 5);
  scene.add(pointLight2);

  camera.position.z = 5;

  // Animate
  const animate = () => {
    requestAnimationFrame(animate);

    shape.rotation.x += 0.01;
    shape.rotation.y += 0.01;

    renderer.render(scene, camera);
  };

  animate();

  // Resize
  const handleResize = () => {
    camera.aspect = canvas.clientWidth / canvas.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  };

  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
};


const PromoteProduct = () => {
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [sellerCaption, setSellerCaption] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPaystackReady, setIsPaystackReady] = useState(false);
  const canvasRef = useRef(null);

  const user = auth.currentUser;

  // Load Paystack script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    script.onload = () => setIsPaystackReady(true);
    script.onerror = () => toast.error("Failed to load Paystack script.");
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      setIsPaystackReady(false);
    };
  }, []);

  // Init Three.js background
  useEffect(() => {
    if (canvasRef.current) {
      const cleanup = initThreeJS(canvasRef.current);
      return cleanup;
    }
  }, []);

  // Fetch products & promotions
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      try {
        const productSnapshot = await getDocs(collection(db, "Products"));
        const userProducts = productSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((data) => data.sellerId === user.uid);

        setProducts(userProducts);

        const promoQuery = query(collection(db, "promotions"), where("userId", "==", user.uid));
        const promoSnapshot = await getDocs(promoQuery);
        setPromotions(promoSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load your data.");
      }
    };

    fetchUserData();
  }, [user]);

  const calculateTotal = () =>
    selectedPlatforms.reduce((sum, p) => sum + platformPricing[p], 0);

  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const processPromotion = (response) => {
    if (!user) return toast.error("User not authenticated.");

    setLoading(true);
    const promoRef = doc(db, "promotions", response.reference);

    setDoc(promoRef, {
      userId: user.uid,
      productId: selectedProductId,
      platforms: selectedPlatforms,
      amount: calculateTotal(),
      status: "pending",
      createdAt: new Date().toISOString(),
      paymentRef: response.reference,
      caption: sellerCaption,
    })
      .then(() =>
        setDoc(doc(db, "Products", selectedProductId), { promoted: true }, { merge: true })
      )
      .then(() => {
        toast.success("Promotion successful! Your product will be posted in 24 hours.");
        setSelectedProductId("");
        setSelectedPlatforms([]);
        setSellerCaption("");
        return getDocs(query(collection(db, "promotions"), where("userId", "==", user.uid)));
      })
      .then((promoSnapshot) => {
        setPromotions(promoSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => {
        console.error("Error saving promotion:", err);
        toast.error("Promotion failed.");
      })
      .finally(() => setLoading(false));
  };

  const handlePay = () => {
    if (!window.PaystackPop || !isPaystackReady) {
      toast.error("Payment service unavailable.");
      return;
    }
    if (!user?.email) return toast.error("You must be logged in.");
    if (!selectedProductId || selectedPlatforms.length === 0)
      return toast.error("Select product and platform(s).");

    const amount = calculateTotal();
    const ref = "" + Math.floor(Math.random() * 1000000000 + 1);

    const handler = window.PaystackPop.setup({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: user.email,
      amount: amount * 100,
      currency: "NGN",
      ref,
      metadata: {
        caption: sellerCaption,
        productId: selectedProductId,
        platforms: selectedPlatforms.join(", "),
      },
      callback: (response) => processPromotion(response),
      onClose: () => toast.info("Transaction canceled."),
    });

    handler.openIframe();
  };

  return (
    <div className="">
      {/* Three.js Canvas Background */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 w-screen h-screen -z-10 block"
      />
      <div className="max-w-2xl mx-auto p-5 mt-10 bg-white/10 text-white rounded shadow">
        <h2 className="text-2xl font-bold mb-6">Promote Your Product</h2>

        <label className="block mb-2">Choose a Product</label>
        <select
          value={selectedProductId}
          onChange={(e) => setSelectedProductId(e.target.value)}
          className="w-full p-2 rounded text-black mb-4"
        >
          <option value="">-- Select Product --</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <div className="mb-4">
          <label className="block mb-2">Select Platform(s)</label>
          {Object.entries(platformPricing).map(([platform, price]) => (
            <div key={platform} className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={selectedPlatforms.includes(platform)}
                onChange={() => handlePlatformToggle(platform)}
                id={platform}
              />
              <label htmlFor={platform} className="capitalize">
                {platform} – ₦{price}
              </label>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <label className="block mb-1">Product Caption</label>
          <textarea
            rows={3}
            value={sellerCaption}
            onChange={(e) => setSellerCaption(e.target.value)}
            className="w-full p-2 rounded text-black"
            placeholder="Enter a short caption..."
          ></textarea>
        </div>

        <div className="mb-4">
          <strong>Total: ₦{calculateTotal()}</strong>
        </div>

        <button
          onClick={handlePay}
          disabled={loading || !isPaystackReady}
          className={`w-full py-2 rounded ${
            loading || !isPaystackReady ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"
          } text-white font-bold transition`}
        >
          {loading ? "Processing..." : "Pay & Promote"}
        </button>

        {/* Latest Promotions */}
        <div className="mt-10">
          <h3 className="text-xl font-bold mb-3">Your Latest Promotions</h3>
          {promotions.length === 0 ? (
            <p>No promotions yet.</p>
          ) : (
            <ul className="space-y-3">
              {promotions.map((promo) => (
                <li
                  key={promo.id}
                  className="bg-white/20 p-3 rounded border border-white/10"
                >
                  <p><strong>Product ID:</strong> {promo.productId}</p>
                  <p><strong>Platforms:</strong> {promo.platforms.join(", ")}</p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <span
                      className={
                        promo.status === "approved"
                          ? "text-green-400"
                          : "text-yellow-300"
                      }
                    >
                      {promo.status}
                    </span>
                  </p>
                  <p><strong>Caption:</strong> {promo.caption}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default PromoteProduct;
